import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Select, Input, DatePicker, TimePicker, message, Spin } from 'antd';
import { CalendarOutlined, EnvironmentOutlined, LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_BASE_URL } from "../config/apiconfig";


const { Option } = Select;
const { TextArea } = Input;

// Replace with your actual Mapbox access token
const MAPBOX_ACCESS_TOKEN = 'your_mapbox_access_token_here';

const EditEventModal = ({ visible, onCancel, event, recruiterId }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cities, setCities] = useState([]);
    const [citySearch, setCitySearch] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        template_id: null,
        event_name: '',
        event_type: 'Virtual',
        event_url: '',
        event_location: '',
        duration: 60,
        scheduled_time: dayjs(),
        notes: '',
        status: 'Scheduled'
    });

    useEffect(() => {
        if (visible && event) {
            fetchTemplates();
            // Populate form with event data
            setFormData({
                template_id: event.template_id || null,
                event_name: event.event_name || '',
                event_type: event.event_type || 'Virtual',
                event_url: event.event_url || '',
                event_location: event.event_location || '',
                duration: event.duration || 60,
                scheduled_time: event.scheduled_time ? dayjs(event.scheduled_time) : dayjs(),
                notes: event.notes || '',
                status: event.status || 'Scheduled'
            });
        }
    }, [visible, event]);

    const fetchTemplates = async () => {
        try {
            console.log('Fetching templates for recruiter:', recruiterId);
            const response = await axios.get(`${API_BASE_URL}/api/templates/events?created_by=${recruiterId}`);
            console.log('Templates response:', response.data);
            setTemplates(response.data);
        } catch (error) {
            console.error('Failed to load event templates:', error);
            console.error('Error response:', error.response?.data);
            message.error('Failed to load event templates');
        }
    };

    const handleTemplateChange = (templateId) => {
        console.log('Selected template ID:', templateId);
        const selectedTemplate = templates.find(t => t.id === templateId);
        console.log('Selected template:', selectedTemplate);
        
        if (selectedTemplate) {
            setFormData(prev => ({
                ...prev,
                template_id: templateId,
                event_name: selectedTemplate.title,
                event_type: selectedTemplate.event_type || 'Virtual',
                duration: selectedTemplate.duration || 60,
                notes: selectedTemplate.description || ''
            }));
        }
    };

    // Debounced function to search cities
    const debouncedCitySearch = useCallback(
        debounce(async (query) => {
            if (query.length < 2) {
                setCities([]);
                return;
            }
            
            setSearchLoading(true);
            try {
                const response = await axios.get(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
                    {
                        params: {
                            access_token: MAPBOX_ACCESS_TOKEN,
                            types: 'place,locality,neighborhood,address',
                            limit: 10,
                            // You can add country restriction if needed
                            // country: 'US,CA,GB', // example for US, Canada, UK
                        }
                    }
                );
                
                const locations = response.data.features.map(feature => ({
                    id: feature.id,
                    name: feature.place_name,
                    shortName: feature.text,
                    coordinates: feature.center,
                    context: feature.context || []
                }));
                
                setCities(locations);
            } catch (error) {
                console.error('Error fetching cities:', error);
                if (error.response?.status === 401) {
                    message.error('Invalid Mapbox API token. Please check your configuration.');
                } else {
                    message.error('Failed to search locations');
                }
                setCities([]);
            } finally {
                setSearchLoading(false);
            }
        }, 300),
        []
    );

    const handleCitySearch = (value) => {
        setCitySearch(value);
        if (value.trim()) {
            debouncedCitySearch(value);
        } else {
            setCities([]);
        }
    };

    const handleLocationSelect = (value, option) => {
        setFormData({
            ...formData, 
            event_location: value
        });
        setCitySearch('');
    };

    const handleSubmit = async () => {
        console.log('=== EDIT EVENT SUBMISSION START ===');
        console.log('Form data:', formData);
        console.log('Event ID:', event.id);
        console.log('Recruiter ID:', recruiterId);

        // Enhanced validation
        if (!formData.event_name || formData.event_name.trim() === '') {
            message.error('Event name is required');
            return;
        }

        if (formData.event_type === 'Virtual' && (!formData.event_url || formData.event_url.trim() === '')) {
            message.error('Event URL is required for virtual events');
            return;
        }

        if (formData.event_type === 'On-site' && (!formData.event_location || formData.event_location.trim() === '')) {
            message.error('Location is required for on-site events');
            return;
        }

        if (!formData.scheduled_time || !formData.scheduled_time.isValid()) {
            message.error('Please select a valid date and time');
            return;
        }

        if (!event.id) {
            message.error('Event ID is missing');
            return;
        }

        if (!recruiterId) {
            message.error('Recruiter ID is missing');
            return;
        }

        // Prepare payload
        const payload = {
            template_id: formData.template_id,
            event_name: formData.event_name.trim(),
            event_type: formData.event_type,
            event_url: formData.event_type === 'Virtual' ? formData.event_url.trim() : null,
            event_location: formData.event_type === 'On-site' ? formData.event_location.trim() : null,
            duration: parseInt(formData.duration) || 60,
            scheduled_time: formData.scheduled_time.format('YYYY-MM-DD HH:mm:ss'),
            notes: formData.notes ? formData.notes.trim() : '',
            status: formData.status,
            updated_by: recruiterId
        };

        console.log('Payload to send:', payload);

        setLoading(true);
        try {
            console.log(`Sending PUT request to /api/events/${event.id}...`);
            const response = await axios.put(`${API_BASE_URL}/api/events/${event.id}`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            console.log('Response received:', response.data);
            message.success('Event updated successfully!');
            onCancel(true); // Pass true to indicate success
        } catch (error) {
            console.error('=== ERROR DETAILS ===');
            console.error('Error object:', error);
            console.error('Error message:', error.message);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            console.error('Error headers:', error.response?.headers);
            
            // More specific error messages
            if (error.response?.status === 401) {
                message.error('Authentication failed. Please login again.');
            } else if (error.response?.status === 404) {
                message.error('Event not found. Please refresh and try again.');
            } else if (error.response?.status === 400) {
                message.error(`Invalid data: ${error.response.data?.message || 'Please check your inputs'}`);
            } else if (error.response?.status === 500) {
                message.error(`Server error: ${error.response.data?.message || 'Please try again later'}`);
            } else if (error.code === 'ECONNREFUSED') {
                message.error('Cannot connect to server. Please check if the backend is running.');
            } else {
                message.error(`Failed to update event: ${error.response?.data?.message || error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Edit Event"
            visible={visible}
            onCancel={() => onCancel(false)}
            footer={[
                <Button key="cancel" onClick={() => onCancel(false)}>
                    Cancel
                </Button>,
                <Button 
                    key="submit" 
                    type="primary" 
                    loading={loading}
                    onClick={handleSubmit}
                >
                    Update Event
                </Button>
            ]}
            width={700}
        >
            <div className="space-y-4">
                <div>
                    <label className="block mb-1 font-medium">Event Status</label>
                    <Select
                        className="w-full"
                        value={formData.status}
                        onChange={status => setFormData({...formData, status})}
                    >
                        <Option value="Scheduled">Scheduled</Option>
                        <Option value="In-progress">In Progress</Option>
                        <Option value="Completed">Completed</Option>
                        <Option value="Cancelled">Cancelled</Option>
                    </Select>
                </div>

                <div>
                    <label className="block mb-1 font-medium">Select an event template</label>
                    <Select
                        className="w-full"
                        placeholder="Select template"
                        onChange={handleTemplateChange}
                        value={formData.template_id}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                    >
                        {templates.map(template => (
                            <Option key={template.id} value={template.id}>
                                {template.title} ({template.event_type || 'Virtual'})
                            </Option>
                        ))}
                    </Select>
                </div>

                <div>
                    <label className="block mb-1 font-medium">Event name *</label>
                    <Input
                        value={formData.event_name}
                        onChange={e => setFormData({...formData, event_name: e.target.value})}
                        placeholder="Enter event name"
                        required
                    />
                </div>

                <div>
                    <label className="block mb-1 font-medium">Event type</label>
                    <Select
                        className="w-full"
                        value={formData.event_type}
                        onChange={type => setFormData({...formData, event_type: type, event_url: '', event_location: ''})}
                    >
                        <Option value="Virtual">Virtual</Option>
                        <Option value="On-site">On-site</Option>
                    </Select>
                </div>

                {formData.event_type === 'Virtual' ? (
                    <div>
                        <label className="block mb-1 font-medium">
                            <LinkOutlined className="mr-2" />
                            Event URL * (eg: Zoom, Google Meets, etc.)
                        </label>
                        <Input
                            value={formData.event_url}
                            onChange={e => setFormData({...formData, event_url: e.target.value})}
                            placeholder="https://zoom.us/j/123456789"
                            required
                        />
                    </div>
                ) : (
                    <div>
                        <label className="block mb-1 font-medium">
                            <EnvironmentOutlined className="mr-2" />
                            Location *
                        </label>
                        <Select
                            className="w-full"
                            showSearch
                            value={formData.event_location}
                            onSearch={handleCitySearch}
                            onChange={handleLocationSelect}
                            placeholder="Search for a city or location"
                            filterOption={false}
                            notFoundContent={
                                searchLoading ? (
                                    <div className="text-center py-2">
                                        <Spin size="small" />
                                        <span className="ml-2">Searching...</span>
                                    </div>
                                ) : citySearch && cities.length === 0 ? (
                                    'No locations found'
                                ) : (
                                    'Type to search locations'
                                )
                            }
                            dropdownRender={(menu) => (
                                <div>
                                    {menu}
                                    {cities.length > 0 && (
                                        <div className="border-t p-2 text-xs text-gray-500">
                                            Powered by Mapbox
                                        </div>
                                    )}
                                </div>
                            )}
                        >
                            {cities.map((city) => (
                                <Option key={city.id} value={city.name} title={city.name}>
                                    <div>
                                        <div className="font-medium">{city.shortName}</div>
                                        <div className="text-sm text-gray-500">{city.name}</div>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-1 font-medium">Duration (minutes)</label>
                        <Input
                            type="number"
                            value={formData.duration}
                            onChange={e => setFormData({...formData, duration: parseInt(e.target.value) || 60})}
                            min="15"
                            max="480"
                        />
                    </div>
                    <div>
                        <label className="block mb-1 font-medium">Date *</label>
                        <DatePicker
                            className="w-full"
                            value={formData.scheduled_time}
                            onChange={date => {
                                if (date) {
                                    // Preserve the time when changing date
                                    const newDateTime = date
                                        .set('hour', formData.scheduled_time.hour())
                                        .set('minute', formData.scheduled_time.minute());
                                    setFormData({...formData, scheduled_time: newDateTime});
                                }
                            }}
                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                        />
                    </div>
                </div>

                <div>
                    <label className="block mb-1 font-medium">Time *</label>
                    <TimePicker
                        className="w-full"
                        format="HH:mm"
                        value={formData.scheduled_time}
                        onChange={time => {
                            if (time) {
                                // Preserve the date when changing time
                                const newDateTime = formData.scheduled_time
                                    .set('hour', time.hour())
                                    .set('minute', time.minute());
                                setFormData({...formData, scheduled_time: newDateTime});
                            }
                        }}
                        minuteStep={15}
                    />
                </div>

                <div>
                    <label className="block mb-1 font-medium">Notes</label>
                    <TextArea
                        rows={3}
                        value={formData.notes}
                        onChange={e => setFormData({...formData, notes: e.target.value})}
                        placeholder="Additional notes about the event..."
                    />
                </div>
            </div>
        </Modal>
    );
};

// Debounce utility function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export default EditEventModal;